import {
  AccountTreeOutlined,
  AddPhotoAlternateOutlined,
  Archive,
  ArchiveOutlined,
  Cancel,
  CategoryOutlined,
  Delete,
  GroupsOutlined,
  InfoOutlined,
  LocalOfferOutlined,
  StickyNote2Outlined,
  StorefrontOutlined,
  Unarchive,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { useUser } from '@sophocles/app';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import CustomerCatalogMatrix from '@sophocles/components/CustomerCatalogMatrix';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ImagePicker from '@sophocles/components/ImagePicker';
import {
  useCreateProductMutation,
  useLazyListProductsQuery,
  useListProductsQuery,
  usePatchProductMutation,
  useUpdateProductPictureMutation,
} from '@sophocles/features/entities/products';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import ComponentMatrix from '@sophocles/views/sales/components/ComponentMatrix';
import ProductCategorySelect from '@sophocles/views/sales/components/ProductCategorySelect';
import ProductFormSection from '@sophocles/views/sales/components/ProductFormSection';

// Form field names, used to attribute API validation errors to their input;
// anything the backend reports outside this list downgrades to the banner.
const PRODUCT_FIELDS = [
  'item_part_num',
  'item_name',
  'item_description',
  'item_cost',
  'item_price',
  'item_weight',
  'low_stock_threshold',
  'has_radios',
  'billing_term_months',
  'default_service',
  'finished_good',
  'used_in_rma',
  'used_in_packing_supplies',
  'vendor_name',
  'vendor_contact',
  'vendor_email',
  'vendor_phone',
  'vendor_part_num',
  'order_qty_min',
  'order_qty_multiple',
  'comments',
  'obsolete',
  'all_customers',
  'customer_ids',
  'is_service',
  'is_access_fee',
  'components',
];

// Dropdown page for the server-searched component picker; narrowing the
// search is the intended path to anything past it.
const COMPONENT_OPTION_LIMIT = 50;

const ProductSchema = Yup.object().shape({
  item_part_num: Yup.string().max(15, 'Too Long!').required('Required'),
  item_name: Yup.string().max(50, 'Too Long!').required('Required'),
  item_description: Yup.string().max(100, 'Too Long!').required('Required'),
  item_cost: Yup.number().required('Required').typeError('Must be a number'),
  item_price: Yup.number().required('Required').typeError('Must be a number'),
  item_weight: Yup.number().required('Required').typeError('Must be a number'),
  // Empty input → null (no low tier). Anything else must be a non-negative whole number.
  low_stock_threshold: Yup.number()
    .transform((value, originalValue) => (originalValue === '' || originalValue == null ? null : value))
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Cannot be negative')
    .nullable(),
  has_radios: Yup.boolean(),
  is_service: Yup.boolean(),
  is_access_fee: Yup.boolean(),
  default_service: Yup.number().nullable(),
  billing_term_months: Yup.number().nullable(),
  used_in_rma: Yup.boolean(),
  used_in_packing_supplies: Yup.boolean(),

  vendor_name: Yup.string().max(40, 'Too Long!').nullable(),
  vendor_contact: Yup.string().max(40, 'Too Long!').nullable(),
  vendor_email: Yup.string().email('Invalid email').max(40, 'Too Long!').nullable(),
  vendor_phone: Yup.string().max(20, 'Too Long!').nullable(),
  vendor_part_num: Yup.string().max(70, 'Too Long!').nullable(),

  order_qty_min: Yup.number().default(0).typeError('Must be a number').nullable(),
  order_qty_multiple: Yup.number().default(0).typeError('Must be a number').nullable(),

  comments: Yup.string().max(200, 'Too Long!').nullable(),
  obsolete: Yup.boolean(),

  components: Yup.array()
    .nullable()
    .of(
      Yup.object().shape({
        id: Yup.number().required('Component ID required').typeError('Must be a number'),
        quantity: Yup.number().required('Quantity required').typeError('Must be a number'),
      }),
    ),
});

function ProductForm({ product, onClose }) {
  const {
    isSuperUser,
    permissions: { isSalesAdmin, isAccounting },
  } = useUser();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [customerNames, setCustomerNames] = React.useState({});
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  const [createProduct] = useCreateProductMutation();
  const [patchProduct] = usePatchProductMutation();
  const [updateProductPicture] = useUpdateProductPictureMutation();
  // Component options are server-searched on demand — the catalog outgrows
  // the grid endpoint's page cap, so a fixed options list silently loses
  // products. Only services are loaded as a list (Default Service select).
  const { data: serviceList = [] } = useListProductsQuery({
    params: { 'filter[is_service]': true, 'filter[obsolete]': false },
  });
  const serviceProducts = Array.isArray(serviceList) ? serviceList : (serviceList.results ?? []);

  const [listProducts] = useLazyListProductsQuery();
  const loadComponentOptions = useCallback(
    search => {
      const params = { 'filter[obsolete]': false, limit: COMPONENT_OPTION_LIMIT };
      const term = search?.trim();
      if (term) params['filter[search]'] = term;
      return listProducts({ params }, true).unwrap();
    },
    [listProducts],
  );

  // Saved components render from the product payload's child_product rows, so
  // no catalog-wide fetch is needed to label them.
  const knownComponentProducts = useMemo(() => product?.components?.map(c => c.child_product) ?? [], [product]);

  const initialValues =
    product != null
      ? {
          ...product,
          // Staged replacement photo (a File) — distinct from `picture`,
          // which holds the existing photo URL. Uploaded separately on save.
          pictureFile: null,
          components: product.components.map(c => ({
            id: c.child_product.id,
            quantity: c.quantity,
          })),
        }
      : {
          pictureFile: null,
          item_part_num: '',
          item_name: '',
          item_description: '',
          item_cost: 0,
          item_price: 0,
          item_weight: 0,
          low_stock_threshold: null,
          has_radios: false,
          billing_term_months: null,
          default_service: null,
          finished_good: false,
          used_in_rma: false,
          used_in_packing_supplies: false,
          vendor_name: '',
          vendor_contact: '',
          vendor_email: '',
          vendor_phone: '',
          vendor_part_num: '',
          order_qty_min: 0,
          order_qty_multiple: 0,
          comments: '',
          obsolete: false,
          all_customers: false,
          customer_ids: [],
          is_service: false,
          is_access_fee: false,
          components: [],
        };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setNonFieldMessages([]);
    try {
      const valueCopy = { ...values };
      // pictureFile is uploaded separately (multipart); never goes in the JSON body.
      const pictureFile = valueCopy.pictureFile;
      delete valueCopy.pictureFile;
      if (valueCopy.is_service && (valueCopy.billing_term_months == null || valueCopy.billing_term_months == 0)) {
        valueCopy.billing_term_months = 0;
        valueCopy.is_access_fee = true;
      }
      let productId = product?.id ?? null;
      if (product) {
        delete valueCopy.picture;
        delete valueCopy.customer_ids;
        const { components } = valueCopy;
        delete valueCopy.components;
        valueCopy.component_data = components;
        await patchProduct({ id: product.id, body: valueCopy }).unwrap();
      } else {
        const { components } = valueCopy;
        delete valueCopy.components;
        valueCopy.component_data = components;
        if (valueCopy.all_customers) {
          delete valueCopy.customer_ids;
        }
        const created = await createProduct({ body: valueCopy }).unwrap();
        productId = created?.id ?? null;
      }
      if (pictureFile && productId != null) {
        // The product write already succeeded; a picture failure must not
        // reopen the save-error path (which would re-run createProduct and
        // duplicate the row). Surface it on its own and still close.
        try {
          await updateProductPicture({ id: productId, file: pictureFile }).unwrap();
        } catch {
          dispatch(
            addToast({
              message: t(
                'warehouse.product.pictureUploadError',
                'Product saved, but the picture could not be uploaded.',
              ),
              level: 'warning',
            }),
          );
        }
      }
      onClose();
    } catch (error) {
      const messages = applyApiErrorToFormik(error, {
        setErrors,
        knownFields: PRODUCT_FIELDS,
        genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
      });
      setNonFieldMessages(messages);
      if (messages.length > 0) {
        dispatch(addToast({ message: t('warehouse.product.saveError', 'Failed to save product'), level: 'error' }));
      }
      setSubmitting(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={ProductSchema} onSubmit={handleSubmit}>
      {({ isSubmitting, values, touched, setFieldValue, submitForm, errors }) => {
        return (
          <Form>
            <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {product
                  ? t('warehouse.product.updateProduct', 'Update Product')
                  : t('warehouse.product.createProduct', 'Create Product')}
              </Typography>

              <FormErrorAlert messages={nonFieldMessages} />

              {product && values.obsolete && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<Unarchive />}
                      onClick={() => setFieldValue('obsolete', false)}
                    >
                      {t('warehouse.product.restore', 'Restore')}
                    </Button>
                  }
                >
                  <AlertTitle>{t('warehouse.product.obsoleteProduct', 'Obsolete Product')}</AlertTitle>
                  {t(
                    'warehouse.product.obsoleteAlertBody',
                    'This product is marked as obsolete. It is hidden from standard views and cannot be ordered.',
                  )}
                </Alert>
              )}

              <ProductFormSection
                icon={<InfoOutlined />}
                title={t('warehouse.product.identityTitle', 'Identity')}
                description={t(
                  'warehouse.product.identityDescription',
                  'How this product is identified, categorized, and described.',
                )}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="item_part_num"
                      as={TextField}
                      label={t('warehouse.product.itemPartNumber', 'Item Part Number')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.item_part_num && Boolean(errors.item_part_num)}
                      helperText={touched.item_part_num && errors.item_part_num}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="item_name"
                      as={TextField}
                      label={t('warehouse.product.itemName', 'Item Name')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.item_name && Boolean(errors.item_name)}
                      helperText={touched.item_name && errors.item_name}
                      required
                    />
                  </Grid>
                  <Grid size={12}>
                    <Field
                      name="item_description"
                      as={TextField}
                      label={t('warehouse.product.itemDescription', 'Item Description')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      error={touched.item_description && Boolean(errors.item_description)}
                      helperText={touched.item_description && errors.item_description}
                      required
                    />
                  </Grid>
                  <Grid size={12}>
                    <ProductCategorySelect
                      values={{
                        product_line: values.product_line,
                        product_family: values.product_family,
                        product_sub_family: values.product_sub_family,
                      }}
                      onChange={(line, family, subFamily) => {
                        setFieldValue('product_line', line?.id);
                        setFieldValue('product_family', family?.id);
                        setFieldValue('product_sub_family', subFamily?.id);
                      }}
                    />
                  </Grid>
                </Grid>
              </ProductFormSection>

              <ProductFormSection
                icon={<AddPhotoAlternateOutlined />}
                title={t('warehouse.product.pictureTitle', 'Product Picture')}
                description={t('warehouse.product.pictureDescription', 'The catalog photo shown for this product.')}
              >
                <ImagePicker
                  currentUrl={values.picture || null}
                  file={values.pictureFile || null}
                  onSelect={file => setFieldValue('pictureFile', file)}
                />
              </ProductFormSection>

              <ProductFormSection
                icon={<CategoryOutlined />}
                title={t('warehouse.product.classificationTitle', 'Classification')}
                description={t(
                  'warehouse.product.classificationDescription',
                  'Flags that drive how the product shows up in orders, inventory, and reporting.',
                )}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormControlLabel
                        sx={{ mb: 0 }}
                        control={
                          <Checkbox
                            name="is_service"
                            checked={values.is_service}
                            color="primary"
                            onChange={() => setFieldValue('is_service', !values.is_service)}
                          />
                        }
                        label={t('warehouse.prepaidService', 'Prepaid Service')}
                      />
                      <FormHelperText>
                        {t(
                          'warehouse.product.prepaidServiceHelper',
                          'A prepaid service offered as an option on radio products.',
                        )}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {values.is_service && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="billing_term_months"
                        as={TextField}
                        label={t('warehouse.product.billingTermMonths', 'Billing Term (Months)')}
                        type="number"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={touched.billing_term_months && Boolean(errors.billing_term_months)}
                        helperText={t(
                          'warehouse.product.billingTermHelper',
                          '0 or blank lets the term be set at order time.',
                        )}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormControlLabel
                        control={<Field name="has_radios" as={Checkbox} color="primary" checked={values.has_radios} />}
                        label={t('warehouse.product.hasRadios', 'Has Radios')}
                      />
                      <FormHelperText>
                        {t('warehouse.product.hasRadiosHelper', 'This product has radios associated with it.')}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {values.has_radios && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={serviceProducts}
                        getOptionLabel={option => (option ? `${option.item_part_num}: ${option.item_name}` : '')}
                        value={
                          values.default_service
                            ? (serviceProducts.find(p => p.id === values.default_service) ?? null)
                            : null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue('default_service', newValue?.id);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('warehouse.product.defaultService', 'Default Service')}
                            variant="outlined"
                            size="small"
                            error={touched.default_service && Boolean(errors.default_service)}
                            helperText={t(
                              'warehouse.product.defaultServiceHelper',
                              'Auto-added as a prepaid item when this product is ordered.',
                            )}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormControlLabel
                        control={
                          <Field name="finished_good" as={Checkbox} color="primary" checked={values.finished_good} />
                        }
                        label={t('warehouse.product.finishedGood', 'Finished Good')}
                      />
                      <FormHelperText>
                        {t(
                          'warehouse.product.finishedGoodHelper',
                          'A complete, sellable assembly (vs. a component / raw part).',
                        )}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormControlLabel
                        control={
                          <Field name="used_in_rma" as={Checkbox} color="primary" checked={values.used_in_rma} />
                        }
                        label={t('warehouse.product.usedInRma', 'Used in RMA')}
                      />
                      <FormHelperText>
                        {t('warehouse.product.usedInRmaHelper', 'Available as an RMA inventory adjustment line item.')}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormControlLabel
                        control={
                          <Field
                            name="used_in_packing_supplies"
                            as={Checkbox}
                            color="primary"
                            checked={values.used_in_packing_supplies}
                          />
                        }
                        label={t('warehouse.product.usedInPackingSupplies', 'Used in Packing Supplies')}
                      />
                      <FormHelperText>
                        {t(
                          'warehouse.product.usedInPackingSuppliesHelper',
                          'Treated as a packing supply rather than a stocked SKU.',
                        )}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </ProductFormSection>

              <ProductFormSection
                icon={<LocalOfferOutlined />}
                title={t('warehouse.product.pricingTitle', 'Pricing & Inventory')}
                description={t(
                  'warehouse.product.pricingDescription',
                  'Cost, list price, weight, and the low-stock threshold used by the Products stats.',
                )}
              >
                <Grid container spacing={2}>
                  {(isSalesAdmin || isAccounting) && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="item_cost"
                        as={TextField}
                        label={t('warehouse.product.itemCost', 'Item Cost')}
                        type="number"
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={touched.item_cost && Boolean(errors.item_cost)}
                        helperText={touched.item_cost && errors.item_cost}
                        required
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="item_price"
                      as={TextField}
                      label={t('warehouse.product.itemPrice', 'Item Price')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={!isSalesAdmin && !isAccounting && !isSuperUser}
                      error={touched.item_price && Boolean(errors.item_price)}
                      helperText={touched.item_price && errors.item_price}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="item_weight"
                      as={TextField}
                      label={t('warehouse.product.itemWeight', 'Item Weight')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.item_weight && Boolean(errors.item_weight)}
                      helperText={touched.item_weight && errors.item_weight}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="low_stock_threshold"
                      as={TextField}
                      label={t('warehouse.product.lowStockThreshold', 'Low-Stock Threshold')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={values.low_stock_threshold ?? ''}
                      onChange={e => {
                        const raw = e.target.value;
                        setFieldValue('low_stock_threshold', raw === '' ? null : Number(raw));
                      }}
                      inputProps={{ min: 0, step: 1 }}
                      error={touched.low_stock_threshold && Boolean(errors.low_stock_threshold)}
                      helperText={
                        (touched.low_stock_threshold && errors.low_stock_threshold) ||
                        t(
                          'warehouse.product.lowStockHelper',
                          'Available qty at or below this value flags the SKU as Low Stock. Leave blank for none.',
                        )
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="order_qty_min"
                      as={TextField}
                      label={t('warehouse.product.orderQtyMin', 'Order Quantity Min')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.order_qty_min && Boolean(errors.order_qty_min)}
                      helperText={
                        (touched.order_qty_min && errors.order_qty_min) ||
                        t('warehouse.product.orderQtyMinHelper', 'Smallest quantity that can be ordered.')
                      }
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="order_qty_multiple"
                      as={TextField}
                      label={t('warehouse.product.orderQtyMultiple', 'Order Quantity Multiple')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.order_qty_multiple && Boolean(errors.order_qty_multiple)}
                      helperText={
                        (touched.order_qty_multiple && errors.order_qty_multiple) ||
                        t(
                          'warehouse.product.orderQtyMultipleHelper',
                          'Order quantity must be a multiple of this value.',
                        )
                      }
                      required
                    />
                  </Grid>
                </Grid>
              </ProductFormSection>

              <ProductFormSection
                icon={<AccountTreeOutlined />}
                title={t('warehouse.product.componentsTitle', 'Components')}
                description={t(
                  'warehouse.product.componentsDescription',
                  'Parts that make up this product (Bill of Materials). Used for finished-good assemblies.',
                )}
              >
                <ComponentMatrix
                  value={values.components}
                  onChange={next => setFieldValue('components', next)}
                  loadOptions={loadComponentOptions}
                  knownProducts={knownComponentProducts}
                />
              </ProductFormSection>

              <ProductFormSection
                icon={<GroupsOutlined />}
                title={t('warehouse.product.customerAccessTitle', 'Customer Access & Pricing')}
                description={t(
                  'warehouse.product.customerAccessDescription',
                  'Access controls who can see/order this product; per-customer overrides apply on top of the base price regardless of access.',
                )}
              >
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="all_customers"
                          checked={values.all_customers}
                          color="primary"
                          onChange={() => setFieldValue('all_customers', !values.all_customers)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {t('warehouse.product.allCustomers', 'All Customers')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {values.all_customers
                              ? t('warehouse.product.allCustomersOn', 'Available to every customer.')
                              : t(
                                  'warehouse.product.allCustomersOff',
                                  'Restricted — only customers with an entry below can see/order this product.',
                                )}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    {product ? (
                      <CustomerCatalogMatrix product={product} />
                    ) : (
                      <Box>
                        <CustomerAutocomplete
                          name="customer_picker"
                          label={t('warehouse.product.addCustomer', 'Add customer')}
                          fullWidth
                          size="small"
                          value={null}
                          onChange={(_e, customerId, opt) => {
                            if (customerId && !values.customer_ids.includes(customerId)) {
                              setFieldValue('customer_ids', [...values.customer_ids, customerId]);
                              if (opt?.label) {
                                setCustomerNames(prev => ({ ...prev, [customerId]: opt.label }));
                              }
                            }
                          }}
                        />
                        {values.customer_ids.length > 0 && (
                          <>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                              {values.customer_ids.map(id => (
                                <Chip
                                  key={id}
                                  label={
                                    customerNames[id] ||
                                    t('warehouse.product.customerNumber', 'Customer #{{id}}', { id })
                                  }
                                  onDelete={() => {
                                    setFieldValue(
                                      'customer_ids',
                                      values.customer_ids.filter(cid => cid !== id),
                                    );
                                  }}
                                  deleteIcon={<Cancel />}
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {t(
                                'warehouse.product.perCustomerPricingNote',
                                'Per-customer pricing overrides can be added after the product is saved.',
                              )}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </ProductFormSection>

              <ProductFormSection
                icon={<StorefrontOutlined />}
                title={t('warehouse.product.vendorTitle', 'Vendor Information')}
                description={t('warehouse.product.vendorDescription', 'Where this product is sourced from.')}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="vendor_name"
                      as={TextField}
                      label={t('warehouse.product.vendorName', 'Vendor Name')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.vendor_name && Boolean(errors.vendor_name)}
                      helperText={touched.vendor_name && errors.vendor_name}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="vendor_contact"
                      as={TextField}
                      label={t('warehouse.product.vendorContact', 'Vendor Contact')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.vendor_contact && Boolean(errors.vendor_contact)}
                      helperText={touched.vendor_contact && errors.vendor_contact}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="vendor_email"
                      as={TextField}
                      label={t('warehouse.product.vendorEmail', 'Vendor Email')}
                      type="email"
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.vendor_email && Boolean(errors.vendor_email)}
                      helperText={touched.vendor_email && errors.vendor_email}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="vendor_phone"
                      as={TextField}
                      label={t('warehouse.product.vendorPhone', 'Vendor Phone')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.vendor_phone && Boolean(errors.vendor_phone)}
                      helperText={touched.vendor_phone && errors.vendor_phone}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="vendor_part_num"
                      as={TextField}
                      label={t('warehouse.product.vendorPartNumber', 'Vendor Part Number')}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched.vendor_part_num && Boolean(errors.vendor_part_num)}
                      helperText={touched.vendor_part_num && errors.vendor_part_num}
                    />
                  </Grid>
                </Grid>
              </ProductFormSection>

              <ProductFormSection
                icon={<StickyNote2Outlined />}
                title={t('warehouse.product.notesTitle', 'Notes')}
                description={t('warehouse.product.notesDescription', 'Internal comments about this product.')}
              >
                <Field
                  name="comments"
                  as={TextField}
                  label={t('warehouse.product.comments', 'Comments')}
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  error={touched.comments && Boolean(errors.comments)}
                  helperText={touched.comments && errors.comments}
                />
              </ProductFormSection>

              {product && (
                <ProductFormSection
                  icon={<ArchiveOutlined />}
                  title={t('warehouse.product.lifecycleTitle', 'Lifecycle')}
                  description={
                    values.obsolete
                      ? t(
                          'warehouse.product.lifecycleObsoleteDescription',
                          'This product is obsolete — hidden from lists and not orderable.',
                        )
                      : t(
                          'warehouse.product.lifecycleActiveDescription',
                          'Retire this product so it stops appearing in lists and cannot be ordered.',
                        )
                  }
                >
                  {values.obsolete ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Unarchive />}
                      onClick={() => setFieldValue('obsolete', false)}
                    >
                      {t('warehouse.product.restoreProduct', 'Restore product')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Archive />}
                      onClick={() => setFieldValue('obsolete', true)}
                    >
                      {t('warehouse.product.markObsolete', 'Mark as Obsolete')}
                    </Button>
                  )}
                </ProductFormSection>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                <Button type="submit" variant="contained" color="primary" onClick={submitForm} disabled={isSubmitting}>
                  {product
                    ? t('warehouse.product.updateProduct', 'Update Product')
                    : t('warehouse.product.createProduct', 'Create Product')}
                </Button>
              </Box>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}

export default ProductForm;
